import { Action, AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  AmplifyErrorObject,
  appAuthenticator,
  AuthenticatorUser,
  PROMISE_REQUEST,
  UserAttributes
} from '@futrli/shared-services';
import { CurrentUserProfile, currentUserQueryKey } from '@futrli/views/queries/currentUser';
import { queryClient } from '@futrli/views/core/queryClient';

import { DispatchAny, StoreState } from '../../core/types';
import {
  GET_CURRENT_AUTHENTICATED_USER,
  SET_PREFERRED_MFA_TYPE,
  STORE_PREFERRED_MFA_TYPE,
  STORE_USER_ATTRIBUTES,
  UPDATE_CURRENT_AUTHENTICATED_USER,
  UPDATE_USER_ATTRIBUTES,
  VERIFY_USER_ATTRIBUTES,
  VERIFY_USER_ATTRIBUTES_SUBMIT
} from '../../auth/constants';
import { StorageDataValue } from '../../storage/types';
import { watchCurrentUserCacheReset } from '../service';

/**
 * Stores the user atrributes on the browser storage.
 * @param {Dispatch<AnyAction>} dispatch
 * @returns {function}
 */
const storeUserAttributes = (dispatch: Dispatch<AnyAction>) => {
  return (attributes: UserAttributes) => {
    dispatch({ type: STORE_USER_ATTRIBUTES, payload: attributes });
  };
};

/**
 * Stores the user atrributes on the browser storage.
 * @param {Dispatch<AnyAction>} dispatch
 * @returns {function}
 */
const storePreferredMfaType = (dispatch: Dispatch<AnyAction>) => {
  return (user: AuthenticatorUser) => {
    dispatch({ type: STORE_PREFERRED_MFA_TYPE, payload: user });
  };
};

/**
 * Captures the current profile query and returns a session guard for delayed auth work.
 * After a cache reset, both the user ID and the original query instance must still match.
 */
const createUserSessionCheck = (user: AuthenticatorUser, hasCacheBeenReset: () => boolean) => {
  const authenticatedUserId = user?.attributes?.sub;
  const profileQuery = queryClient.getQueryCache().find<CurrentUserProfile>({
    exact: true,
    queryKey: currentUserQueryKey()
  });

  return () => {
    if (!hasCacheBeenReset()) {
      return true;
    }
    if (!authenticatedUserId || !profileQuery) {
      return false;
    }

    // The ID alone is not enough: logging out and back in as the same user creates a new query.
    const isSameQuery = queryClient.getQueryCache().get(profileQuery.queryHash) === profileQuery;
    const profileMatchesUser = profileQuery.state.data?.id === authenticatedUserId;
    return isSameQuery && profileMatchesUser;
  };
};

/**
 * Restores a verified profile after auth-boundary middleware clears the cache.
 * The profile, returned auth user and current Redux user must all identify the same user.
 */
const restoreVerifiedUserProfile = (
  profile: CurrentUserProfile | undefined,
  user: AuthenticatorUser,
  state: StoreState
) => {
  const currentReduxUserId = state.authReducers.authenticatedUser?.attributes?.sub;
  const returnedUserId = user?.attributes?.sub;
  if (!profile || profile.id !== currentReduxUserId || currentReduxUserId !== returnedUserId) {
    return;
  }

  queryClient.setQueryData(currentUserQueryKey(), profile);
};

/**
 * Gets the current authenticated user
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 * @returns {Promise} authenticated user
 */
export const getCurrentAuthenticatedUser = (): DispatchAny => {
  /**
   * To be able to manage the getCurrentAuthenticatedUser action state properly, we have to call the
   * auth method directly (instead of using the api middleware) and call the different GET_CURRENT_AUTHENTICATED_USER
   * states manually outside of this function to have full control of the loading for the main container.
   */
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const cacheReset = watchCurrentUserCacheReset();
    // SDK-only authentication also needs a query entry so logout can invalidate this attempt.
    queryClient.getQueryCache().build<unknown>(queryClient, { queryKey: currentUserQueryKey() });
    try {
      const user = (await appAuthenticator.getCurrentAuthenticatedUser()) as AuthenticatorUser;
      const isSameSession = createUserSessionCheck(user, cacheReset.hasBeenReset);
      if (!isSameSession()) {
        return null;
      }

      // Attribute loading is asynchronous too; check again before storing or returning its result.
      await getAndStoreUserAttributes(user, isSameSession)(dispatch);
      return isSameSession() ? user : null;
    } finally {
      cacheReset.unsubscribe();
    }
  };
};

/**
 * Updates the redux store with the state of authenticated user action to pending state.
 * @returns {DispatchAny}
 */
export const setCurrentAuthenticatedUserPending = (): DispatchAny => {
  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({ type: GET_CURRENT_AUTHENTICATED_USER.PENDING });
  };
};

/**
 * Updates the redux store with the state of authenticated user action to fulfilled state.
 * @param {AuthenticatorUser} user
 * @returns {DispatchAny}
 */
export const setCurrentAuthenticatedUserFulfilled = (user: AuthenticatorUser): DispatchAny => {
  return (dispatch: ThunkDispatch<StoreState, unknown, Action>, getState: () => StoreState) => {
    const backendUser =
      appAuthenticator.getCachedCurrentUser() ?? queryClient.getQueryData<CurrentUserProfile>(currentUserQueryKey());

    // Auth-boundary middleware clears Query during this dispatch, including on initial login.
    dispatch({ type: GET_CURRENT_AUTHENTICATED_USER.FULFILLED, payload: user });
    // Restore only the already-verified profile, after that clear and only for the current Redux user.
    restoreVerifiedUserProfile(backendUser, user, getState());
  };
};

/**
 * Updates the redux store authenticated user with given payload.
 * @param {AuthenticatorUser} user
 * @returns {DispatchAny}
 */
export const updateCurrentAuthenticatedUser = (user: AuthenticatorUser): DispatchAny => {
  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({ type: UPDATE_CURRENT_AUTHENTICATED_USER, payload: user });
  };
};

/**
 * Updates the redux store with the state of authenticated user action to rejected state.
 * @returns {DispatchAny}
 */
export const setCurrentAuthenticatedUserRejected = (): DispatchAny => {
  const notAuthenticatedUserError: AmplifyErrorObject = {
    code: 'NotAuthenticated',
    name: 'NotAuthenticated',
    // TODO: Check if it needs i18n
    message: 'not authenticated'
  };

  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({
      type: GET_CURRENT_AUTHENTICATED_USER.REJECTED,
      payload: notAuthenticatedUserError,
      error: true
    });
  };
};

/**
 * Gets the user attributes.
 * @param {AuthenticatorUser} user
 * @returns {Promise} user attributes
 */
export const getUserAttributes = async (user: AuthenticatorUser): Promise<UserAttributes> => {
  return appAuthenticator.getUserAttributes(user) as Promise<UserAttributes>;
};

/**
 * Loads attributes and stores them only if the caller's session guard still passes after the async read.
 */
export const getAndStoreUserAttributes = (user: AuthenticatorUser, isCurrent = () => true) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const attributes: UserAttributes = await getUserAttributes(user);

    if (isCurrent()) {
      storeUserAttributes(dispatch)(attributes);
    }

    return attributes;
  };
};

const getAndStoreUserMfa = (user: AuthenticatorUser) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    storePreferredMfaType(dispatch)(user);

    return user;
  };
};

/**
 * Updates the user attributes.
 * @param {UserAttributes} attributes
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const updateUserAttributes = (attributes: UserAttributes) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: UPDATE_USER_ATTRIBUTES.ACTION,
      payload: appAuthenticator.updateUserAttributes(user, attributes),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserAttributes(user)(dispatch);
  };
};

export const verifyUserAttributes = (attribute: string) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: VERIFY_USER_ATTRIBUTES.ACTION,
      payload: appAuthenticator.verifyUserAttribute(user, attribute),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserAttributes(user)(dispatch);
  };
};

export const verifyUserAttributesSubmit = (attribute: string, code: string) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: VERIFY_USER_ATTRIBUTES_SUBMIT.ACTION,
      payload: appAuthenticator.verifyCurrentUserAttributeSubmit(attribute, code),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserAttributes(user)(dispatch);
  };
};

export const setPreferredMFAType = (type = 'SMS') => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: SET_PREFERRED_MFA_TYPE.ACTION,
      payload: appAuthenticator.setPreferredMFA(user, type),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserMfa({ ...user, preferredMFA: type })(dispatch);
  };
};

/**
 * Updates the user password.
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const updateUserPassword = (oldPassword: string, newPassword: string) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: UPDATE_USER_ATTRIBUTES.ACTION,
      payload: appAuthenticator.changePassword(user, oldPassword, newPassword),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return;
  };
};

/**
 * Updates an specific user atribute.
 * @param {string} key
 * @param {StorageDataValue} value
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const updateUserAttribute = (key: string, value?: StorageDataValue) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) =>
    updateUserAttributes({ [key]: value })(dispatch);
};
